/**
 * Shared types inlined from @axiomgo/shared for standalone deployment.
 * Keep in sync with packages/shared/src/
 */

// ═══ Connection ═══

export type ConnectionState =
  | 'DISCONNECTED'
  | 'CONNECTING'
  | 'CONNECTED'
  | 'RECONNECTING'
  | 'DEGRADED'
  | 'OFFLINE'
  | 'AUTH_ERROR'

export type TransportMode = 'websocket' | 'rest' | 'offline'

export interface ConnectionConfig {
  wsUrl: string
  restUrl: string
  maxWsRetries: number
  baseRetryMs: number
  maxRetryMs: number
  heartbeatMs: number
  healthCheckMs: number
  pingTimeoutMs: number
}

export const DEFAULT_CONNECTION_CONFIG: ConnectionConfig = {
  wsUrl: 'ws://localhost:4000/ws',
  restUrl: 'http://localhost:4000',
  maxWsRetries: 5,
  baseRetryMs: 1000,
  maxRetryMs: 30000,
  // Tuned for Railway/Vercel proxy 55s idle timeout:
  // heartbeat 20s + pingTimeout 20s = 40s max gap, safely below 55s wall.
  heartbeatMs: 20000,
  healthCheckMs: 60000,
  pingTimeoutMs: 20000,
}

// ═══ Messages: Client → Engine ═══

export interface ChatMessagePayload {
  type: 'chat_message'
  content: string
  tenantId: string
  userId: string
  conversationId?: string
}

export interface PingPayload {
  type: 'ping'
  timestamp: number
}

export interface HeartbeatPayload {
  type: 'heartbeat'
  timestamp: number
}

export type ClientPayload = ChatMessagePayload | PingPayload | HeartbeatPayload

// ═══ Messages: Engine → Client ═══

export interface ConnectedEvent {
  type: 'connected'
  serverId: string
  timestamp: string
}

export interface PongEvent {
  type: 'pong'
  timestamp: number
}

export interface HeartbeatAckEvent {
  type: 'heartbeat_ack'
  timestamp: number
}

export interface ChatResponseEvent {
  type: 'chat_response'
  conversationId: string
  message: {
    role: 'assistant'
    content: string
    timestamp: string
  }
  toolCalls?: Array<{
    name: string
    result: unknown
    status: 'approved' | 'pending'
  }>
}

export interface ChatStreamChunkEvent {
  type: 'chat_stream_chunk'
  conversationId: string
  delta: string
  done: boolean
}

export interface ProgressEvent {
  type: 'progress'
  step: 'searching' | 'sources' | 'analyzing' | 'tools' | 'generating' | 'reasoning'
  detail: string
  timestamp: number
}

export interface ErrorEvent {
  type: 'error'
  code: number
  message: string
}

export type ServerEvent =
  | ConnectedEvent
  | PongEvent
  | HeartbeatAckEvent
  | ChatResponseEvent
  | ChatStreamChunkEvent
  | ProgressEvent
  | ErrorEvent

// ═══ REST API Types ═══

export interface ChatRestRequest {
  content: string
  tenantId: string
  userId: string
  conversationId?: string
}

export interface ChatRestResponse {
  conversationId: string
  message: {
    role: 'assistant'
    content: string
    timestamp: string
  }
  toolCalls?: Array<{
    name: string
    result: unknown
    status: 'approved' | 'pending'
  }>
  webSearch?: {
    searched: boolean
    provider: string
    source_count: number
    cache_hit: boolean
    sources: Array<{
      title: string
      url: string
      confidence: number
      domain: string
    }>
    equipment: { manufacturer: string; model: string } | null
  }
}
